import type { OtpState, SubmissionStatus, VerifiedState } from './types';

export async function requestOtp(csrfToken: string, email: string): Promise<OtpState> {
    const res = await fetch('/account/request-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-csrftoken': csrfToken },
        body: JSON.stringify({ csrftoken: csrfToken, email }),
    });
    const json = await res.json();
    if (!res.ok) { throw new Error(json.error || `Request failed (${res.status})`); }
    return json as OtpState;
}

export async function verifyOtp(
    csrfToken: string,
    email: string,
    code: string,
    otpState: OtpState,
): Promise<VerifiedState> {
    const res = await fetch('/account/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-csrftoken': csrfToken },
        body: JSON.stringify({ csrftoken: csrfToken, email, code, otp_id: otpState.otp_id }),
    });
    const json = await res.json();
    if (!res.ok) { throw new Error(json.error || `Request failed (${res.status})`); }
    return json as VerifiedState;
}

export interface ApplicationFormData {
    first_name: string;
    last_name: string;
    insta_handle: string;
    tiktok_handle: string;
    primary_sport: string;
    athlete_level: string;
    city: string;
    state: string;
    country: string;
    referrer: string;
    heard_about_source: string;
    referral_code: string | null;
    extra_data: Record<string, string[]>;
}

export async function checkApplicationStatus(
    csrfToken: string,
    email: string,
    applicationId: string,
    verifiedState: VerifiedState,
): Promise<{ status: SubmissionStatus | 'no_submission' }> {
    const res = await fetch(`/apply/${applicationId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-csrftoken': csrfToken },
        body: JSON.stringify({
            email,
            check_only: true,
            verified_token: verifiedState.verified_token,
            verified_at: verifiedState.verified_at,
        }),
    });
    const json = await res.json();
    if (!res.ok) { throw new Error(json.error || `Request failed (${res.status})`); }
    return json;
}

export type SubmitApplicationResult =
    | { ok: true; status: SubmissionStatus }
    | { ok: false; error: string };

export async function submitApplication(
    csrfToken: string,
    email: string,
    applicationId: string,
    form: ApplicationFormData,
    verifiedState: VerifiedState,
    forceReapply: boolean,
): Promise<SubmitApplicationResult> {
    try {
        const res = await fetch(`/apply/${applicationId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'x-csrftoken': csrfToken },
            body: JSON.stringify({
                csrftoken: csrfToken,
                email,
                first_name: form.first_name,
                last_name: form.last_name,
                insta_handle: form.insta_handle,
                tiktok_handle: form.tiktok_handle,
                primary_sport: form.primary_sport,
                athlete_level: form.athlete_level,
                city: form.city,
                state: form.state,
                country: form.country,
                referrer: form.referrer,
                heard_about_source: form.heard_about_source,
                referral_code: form.referral_code,
                extra_data: Object.keys(form.extra_data).length > 0 ? form.extra_data : undefined,
                verified_token: verifiedState.verified_token,
                verified_at: verifiedState.verified_at,
                force_reapply: forceReapply,
            }),
        });
        const json = await res.json();
        if (!res.ok) { return { ok: false, error: json.error || 'Something went wrong. Please try again.' }; }
        return { ok: true, status: json.status };
    } catch {
        return { ok: false, error: 'Something went wrong. Please try again.' };
    }
}
